import React, { useEffect, useState } from 'react';
import TuMesaYaForm from '../components/Form/TuMesaYaForm';
import { useTracking } from '../hooks/useTracking';
import { submitLeadToWebhook } from '../utils/api';
import { FormData } from '../utils/validation';
import logo from '../shared/assets/images/logo-tumesaya.svg';
import reservationsImage from '../shared/assets/images/img-ventaja-reservas247.avif';
import './landing.css';

type SubmitState = 'idle' | 'success' | 'error';

const TrialPage: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const { trackPageView } = useTracking();

  useEffect(() => {
    trackPageView('prueba-30-minutos', 'telemarketing');
  }, [trackPageView]);

  const handleTrialSubmit = async (formData: FormData) => {
    setIsSubmitting(true);
    setSubmitState('idle');

    const response = await submitLeadToWebhook({
      ...formData,
      source: 'telemarketing-trial-30min',
      timestamp: new Date().toISOString(),
    });

    setIsSubmitting(false);
    setSubmitState(response.success ? 'success' : 'error');
    return response.success;
  };

  return (
    <div className="trial-page">
      <header className="trial-header">
        <a className="brand" href="/" aria-label="TuMesaYa, volver al inicio">
          <img src={logo} alt="TuMesaYa" />
        </a>
        <a className="text-link" href="/">
          Volver a la web <span aria-hidden="true">→</span>
        </a>
      </header>

      <main className="trial-main">
        <section className="trial-intro">
          <div className="trial-intro__copy">
            <p className="eyebrow">Prueba exclusiva · 30 minutos</p>
            <h1>
              Prueba cómo TuMesaYa atendería las reservas de tu restaurante.
            </h1>
            <p className="trial-intro__lead">
              Si ya has hablado con nuestro equipo, completa tus datos para
              activar una prueba gratuita y conocer el asistente de teléfono en
              acción. El asistente de WhatsApp llegará próximamente.
            </p>
            <ul className="trial-benefits">
              <li>
                <strong>30 minutos</strong>
                <span>para probar el servicio</span>
              </li>
              <li>
                <strong>Sin tarjeta</strong>
                <span>y sin compromiso</span>
              </li>
              <li>
                <strong>Preparada para ti</strong>
                <span>con los datos de tu restaurante</span>
              </li>
            </ul>
            <div className="trial-visual">
              <img
                src={reservationsImage}
                alt="Persona realizando una reserva desde su teléfono"
              />
              <div>
                <span className="live-dot" />
                <strong>Reservas atendidas 24/7</strong>
              </div>
            </div>
          </div>

          <div className="form-card form-card--trial">
            {submitState === 'success' ? (
              <div className="form-message form-message--success" role="status">
                <span>✓</span>
                <h2>Registro recibido</h2>
                <p>
                  Ya tenemos tus datos. Nuestro equipo te indicará el siguiente
                  paso para poner en marcha la prueba.
                </p>
                <a className="button button--dark" href="/">
                  Volver a TuMesaYa
                </a>
              </div>
            ) : (
              <>
                <div className="form-card__heading">
                  <span>Activa tu acceso</span>
                  <strong>Prueba gratuita de 30 minutos</strong>
                </div>
                <TuMesaYaForm
                  onSubmit={handleTrialSubmit}
                  isSubmitting={isSubmitting}
                  variant="trial"
                  compact
                  submitLabel="Activar mi prueba de 30 min"
                />
                {submitState === 'error' && (
                  <p className="form-error" role="alert">
                    No hemos podido completar el registro. Inténtalo de nuevo o
                    llama al 911 09 53 89.
                  </p>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      <footer className="trial-footer">
        <span>© {new Date().getFullYear()} TuMesaYa</span>
        <a href="/politica-privacidad">Política de privacidad</a>
        <a href="/aviso-legal">Aviso legal</a>
        <a href="/terminos">Términos y condiciones</a>
        <a href="/cookies">Política de cookies</a>
        <button type="button" data-cc="show-preferencesModal">
          Configurar cookies
        </button>
        <a href="tel:+34911095389">911 09 53 89</a>
      </footer>
    </div>
  );
};

export default TrialPage;
